import { Pill, PILL_COLORS } from "components/ui/Pill";

import {
  getReviewTypeTitle,
  PROGRAMME_LABELS,
  PROGRAMMES,
} from "../../constants/reviewTypes";
import {
  getAllowedActionsByGroup,
  getDeniedActionsByGroup,
} from "../../utils/allowedActions";
import style from "./CdmProgrammeAvailability.module.css";

// Surfaces the backend's per-programme decision (allowedActions / deniedActions
// from GET /cdm/patients/{id}/status) alongside the review history. Without it
// the reason a programme cannot be started is only visible inside the Select
// Programme dialog — which is not reachable at all once every programme is
// denied, because the "Start review" button is then not rendered.
//
// An available programme doubles as a shortcut: clicking it opens the picker
// already on that programme.
export const CdmProgrammeAvailability = ({
  allowedActions,
  deniedActions,
  onStartReview,
}) => {
  const allowedGroups = getAllowedActionsByGroup(allowedActions);
  const deniedGroups = getDeniedActionsByGroup(deniedActions);

  const programmes = PROGRAMMES.map((programme) => ({
    programme,
    allowed: allowedGroups[programme],
    reason: deniedGroups[programme]?.[0]?.reason ?? null,
  })).filter(({ allowed, reason }) => allowed.length > 0 || !!reason);

  if (!programmes.length) return null;

  return (
    <section className={style.availability}>
      <h3 className={style.title}>Programme availability</h3>

      <ul className={style.programmes}>
        {programmes.map(({ programme, allowed, reason }) => {
          const isAvailable = allowed.length > 0;
          const isClickable = isAvailable && !!onStartReview;

          // With a single allowed review type the dialog can open already on it;
          // with several, the user still has to choose which one.
          const handleClick = () =>
            onStartReview(allowed.length === 1 ? allowed[0].reviewType : "");

          if (isClickable) {
            return (
              <li key={programme}>
                <button
                  type="button"
                  className={`${style.programme} ${style.programmeButton}`}
                  onClick={handleClick}
                >
                  <span className={style.header}>
                    <span className={style.programmeName}>
                      {PROGRAMME_LABELS[programme]}
                    </span>
                    <Pill {...PILL_COLORS.statusSuccess}>Available</Pill>
                  </span>

                  <span className={style.actions}>
                    {allowed.map((action) => (
                      <Pill key={action.reviewType} {...PILL_COLORS.method}>
                        {getReviewTypeTitle(action.reviewType)}
                      </Pill>
                    ))}
                  </span>
                </button>
              </li>
            );
          }

          return (
            <li key={programme} className={style.programme}>
              <div className={style.header}>
                <p className={style.programmeName}>
                  {PROGRAMME_LABELS[programme]}
                </p>
                <Pill
                  {...(isAvailable
                    ? PILL_COLORS.statusSuccess
                    : PILL_COLORS.statusNoStatus)}
                >
                  {isAvailable ? "Available" : "Not available"}
                </Pill>
              </div>

              {isAvailable && (
                <div className={style.actions}>
                  {allowed.map((action) => (
                    <Pill key={action.reviewType} {...PILL_COLORS.method}>
                      {getReviewTypeTitle(action.reviewType)}
                    </Pill>
                  ))}
                </div>
              )}

              {!!reason && <p className={style.reason}>{reason}</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
};
